/**
 * SessionHistoryTable — Shared paginated session history table.
 *
 * Used by:
 *   Training Portal: /app/history  (portal='training')
 *   Admin Portal:    /admin/agents/{id}  (portal='admin')
 *
 * The "View" link resolves differently per portal:
 *   training → /app/session/{id}/summary
 *   admin    → /admin/sessions/{id}
 *
 * EPIC-04-05 AC-1, AC-5, AC-6
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export interface SessionRow {
  session_id: string;
  scenario_title: string;
  started_at: string;        // ISO datetime string
  duration_secs: number | null;
  overall_score: number;
  passed: boolean;
  violation_count: number;
}

export interface SessionHistoryTableProps {
  sessions: SessionRow[];
  portal: 'training' | 'admin';
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  i18nNs?: string;
  keyPrefix?: string;
}

export function SessionHistoryTable({
  sessions,
  portal,
  page,
  totalPages,
  onPageChange,
  i18nNs = 'history',
  keyPrefix,
}: SessionHistoryTableProps): JSX.Element {
  const { t } = useTranslation(i18nNs, keyPrefix ? { keyPrefix } : undefined);
  const navigate = useNavigate();

  const handleView = (sessionId: string) => {
    const path =
      portal === 'training'
        ? `/app/session/${sessionId}/summary`
        : `/admin/sessions/${sessionId}`;
    navigate(path);
  };

  const formatScore = (score: number) => `${(score * 100).toFixed(1)}%`;
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  if (sessions.length === 0) {
    return (
      <div
        style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#888',
          border: '1px dashed #ccc',
          borderRadius: 8,
        }}
        data-testid="session-table-empty"
      >
        {t('noSessions')}
      </div>
    );
  }

  return (
    <div>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}
        data-testid="session-history-table"
      >
        <thead>
          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
            <th style={thStyle}>{t('columns.scenario')}</th>
            <th style={thStyle}>{t('columns.date')}</th>
            <th style={thStyle}>{t('columns.score')}</th>
            <th style={thStyle}>{t('columns.status')}</th>
            <th style={thStyle}>{t('columns.violations')}</th>
            <th style={thStyle}>{t('columns.action')}</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((row) => (
            <tr
              key={row.session_id}
              style={{ borderBottom: '1px solid #e2e8f0' }}
            >
              <td style={tdStyle}>{row.scenario_title || '—'}</td>
              <td style={tdStyle}>{formatDate(row.started_at)}</td>
              <td style={tdStyle}>{formatScore(row.overall_score)}</td>
              <td style={tdStyle}>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    backgroundColor: row.passed ? '#dcfce7' : '#fee2e2',
                    color: row.passed ? '#16a34a' : '#dc2626',
                  }}
                  data-testid={row.passed ? 'badge-passed' : 'badge-failed'}
                >
                  {row.passed ? t('passedBadge') : t('failedBadge')}
                </span>
              </td>
              <td style={tdStyle}>{row.violation_count}</td>
              <td style={tdStyle}>
                <button
                  onClick={() => handleView(row.session_id)}
                  style={viewBtnStyle}
                  data-testid={`view-${row.session_id}`}
                >
                  {t('viewButton')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            justifyContent: 'center',
          }}
          data-testid="pagination"
        >
          <button
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            style={{ padding: '4px 12px', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
          >
            ‹
          </button>
          <span data-testid="pagination-label">
            {t('pagination', { page, total: totalPages })}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            style={{
              padding: '4px 12px',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#374151',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'middle',
};

const viewBtnStyle: React.CSSProperties = {
  padding: '4px 12px',
  border: '1px solid #2563eb',
  borderRadius: 4,
  background: 'transparent',
  color: '#2563eb',
  cursor: 'pointer',
  fontSize: '0.85rem',
};
